import { useState } from "react";
import { trackEvent } from "../components/Analytics.js";

const initialForm = {
  name: "",
  phone: "",
  email: "",
  business: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle");

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const submitForm = async (event) => {
    event.preventDefault();
    setStatus("sending");

    trackEvent("contact_form_started", {
      form_name: "contact_form",
      page_path: window.location.pathname,
    });

    try {
      const response = await fetch("https://formspree.io/f/mzeblzge", {
        method: "POST",
        body: new FormData(event.currentTarget),
        headers: { Accept: "application/json" },
      });

      if (!response.ok) throw new Error("Submission failed");

      setForm(initialForm);
      setStatus("success");
      trackEvent("generate_lead", {
        form_name: "contact_form",
        page_path: window.location.pathname,
      });
    } catch {
      setStatus("error");
    }
  };

  const inputClasses =
    "mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-containx-navy outline-none transition placeholder:text-slate-400 focus:border-containx-tan focus:ring-2 focus:ring-containx-tan/30";

  return (
    <main className="min-h-screen bg-slate-50 text-containx-navy">
      <section className="bg-containx-navy px-6 py-12 text-center text-white md:py-16">
        <p className="mb-3 text-sm font-bold uppercase tracking-widest text-containx-beige">We’re here to help</p>
        <h1 className="text-4xl font-bold md:text-5xl">Contact ContainX</h1>
        <p className="mx-auto mt-4 max-w-2xl text-lg leading-8 text-slate-200">
          Ask about products, availability, bulk orders, delivery, or scheduling a visit to see samples.
        </p>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-6 py-12 lg:grid-cols-5 lg:py-16">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5 sm:p-8 lg:col-span-3">
          <h2 className="text-3xl font-semibold">Send us a message</h2>
          <p className="mt-2 text-slate-600">Complete the form and our team will respond as soon as possible.</p>

          <form onSubmit={submitForm} className="mt-8 space-y-5">
            <input type="hidden" name="_subject" value="New ContainX website inquiry" />
            <input type="text" name="_gotcha" className="hidden" tabIndex="-1" autoComplete="off" aria-hidden="true" />
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block font-semibold">
                Name <span className="text-red-700">*</span>
                <input name="name" type="text" autoComplete="name" required value={form.name} onChange={updateField} className={inputClasses} placeholder="Your name" />
              </label>
              <label className="block font-semibold">
                Business
                <input name="business" type="text" autoComplete="organization" value={form.business} onChange={updateField} className={inputClasses} placeholder="Restaurant or company" />
              </label>
              <label className="block font-semibold">
                Phone
                <input name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={updateField} className={inputClasses} placeholder="(301) 555-0123" />
              </label>
              <label className="block font-semibold">
                Email <span className="text-red-700">*</span>
                <input name="email" type="email" autoComplete="email" required value={form.email} onChange={updateField} className={inputClasses} placeholder="you@business.com" />
              </label>
            </div>
            <label className="block font-semibold">
              How can we help? <span className="text-red-700">*</span>
              <textarea name="message" required rows="6" value={form.message} onChange={updateField} className={inputClasses} placeholder="Tell us what products, quantities, or information you need." />
            </label>
            {status === "success" && <div role="status" className="rounded-lg border border-green-300 bg-green-50 p-4 font-semibold text-green-800">Thank you! Your message was sent successfully. We’ll be in touch soon.</div>}
            {status === "error" && <div role="alert" className="rounded-lg border border-red-300 bg-red-50 p-4 text-red-800">We couldn’t send your message. Please try again or call us at <a href="tel:+13019698766" className="font-semibold underline">(301) 969-8766</a>.</div>}
            <button type="submit" disabled={status === "sending"} className="w-full rounded-lg bg-containx-navy px-6 py-3 font-bold text-white transition hover:bg-containx-charcoal disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
              {status === "sending" ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>

        <aside className="space-y-6 lg:col-span-2">
          <div className="rounded-2xl bg-containx-beige p-6 sm:p-8">
            <h2 className="text-2xl font-semibold">Contact directly</h2>
            <div className="mt-6 space-y-5">
              <div><p className="text-sm font-bold uppercase tracking-wider">Call or text</p><a href="tel:+13019698766" className="mt-1 block text-xl font-semibold hover:underline">(301) 969-8766</a></div>
              <div><p className="text-sm font-bold uppercase tracking-wider">Email</p><a href="mailto:sales@containx.co" className="mt-1 block text-xl font-semibold hover:underline">sales@containx.co</a></div>
              <div><p className="text-sm font-bold uppercase tracking-wider">Visit us</p><a href="https://www.google.com/maps/place/7377+Washington+Blvd+%23107,+Elkridge,+MD+21075" target="_blank" rel="noopener noreferrer" className="mt-1 block leading-7 hover:underline">7377 Washington Blvd, Suite 107<br />Elkridge, MD 21075</a></div>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5 sm:p-8">
            <h2 className="text-2xl font-semibold">Business hours</h2>
            <dl className="mt-5 space-y-3">
              <div className="flex justify-between gap-4"><dt>Monday–Friday</dt><dd className="font-semibold">10 AM–5 PM</dd></div>
              <div className="flex justify-between gap-4 border-t border-slate-200 pt-3"><dt>Saturday–Sunday</dt><dd className="font-semibold">Closed</dd></div>
            </dl>
            <p className="mt-5 text-sm leading-6 text-slate-600">Please contact us before visiting so we can have the right samples ready.</p>
          </div>
        </aside>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-16">
        <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-black/5">
          <iframe
            className="h-80 w-full border-0"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3092.7048535548643!2d-76.76715592404955!3d39.18142997166482!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7e105e691b82f%3A0x753a837e2d23ae07!2sContainX!5e0!3m2!1sen!2sus!4v1739198323972!5m2!1sen!2sus"
            title="ContainX location in Elkridge, Maryland"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </main>
  );
};

export default Contact;
